// Prefix search for the "Generate with AI" artist picker. A producer
// types a name and the list narrows to artists whose name STARTS WITH
// what was typed — every extra letter makes the query more specific and
// leaves fewer artists, so a specific artist can be selected quickly.
//
// Pure functions so the match contract is unit-testable independent of
// the dialog render (mirrors crewFunctionFilter.ts).

export type SearchableArtist = { artist_name: string };

// Lowercase, strip diacritics, and trim so the search is forgiving:
// "Émile" matches a query of "emi", casing is ignored, and stray
// leading/trailing whitespace in the query never hides a match.
export function normalizeForSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

// True when the artist's name begins with the (normalized) query.
// An empty / whitespace-only query means "no search active" and matches
// every artist.
export function artistMatchesSearch(artistName: string, query: string): boolean {
  const normalizedQuery = normalizeForSearch(query);
  if (normalizedQuery === "") return true;
  return normalizeForSearch(artistName).startsWith(normalizedQuery);
}

// Filter a list of artists down to those whose name starts with the
// query, preserving input order. An empty query returns the input
// unchanged (no allocation churn while the field is blank). The query
// is normalized once here rather than per artist.
export function filterArtistsBySearch<T extends SearchableArtist>(
  artists: T[],
  query: string,
): T[] {
  const normalizedQuery = normalizeForSearch(query);
  if (normalizedQuery === "") return artists;
  return artists.filter((a) => normalizeForSearch(a.artist_name).startsWith(normalizedQuery));
}
